import { FC, useEffect, useRef } from "react";
import {
  ActivityIndicator,
  StyleProp,
  StyleSheet,
  Text,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle,
} from "react-native";
import { fonts } from "src/fonts";
import { kColors } from "src/kColors";
import { kFonts } from "src/kFonts";
import { kStyleConst } from "src/kStyle";
import { useHover } from "usehooks-ts";

export type tButtonTypes = "primary" | "secondary" | "warning";

export type tButton = {
  style?: StyleProp<ViewStyle>;
  textStyle?: TextStyle;
  text: string;
  onPress?: () => void;
  isLoading?: boolean;
  disabled?: boolean;
  type?: tButtonTypes;
  icon?: JSX.Element;
  iconPlacement?: "left" | "right";
  padLeft?: boolean;
  padRight?: boolean;
  onHoverChange?: (isHover: boolean) => void;
};

const StandardButton: FC<tButton> = ({
  text,
  style,
  onPress: myOnPress,
  textStyle,
  isLoading,
  disabled,
  type,
  icon,
  iconPlacement = "left",
  padLeft,
  padRight,
  onHoverChange,
}) => {
  const kHoverRef = useRef(null);
  const isHover = useHover(kHoverRef);

  useEffect(() => {
    if (onHoverChange) onHoverChange(isHover);
  }, [isHover, onHoverChange]);
  return (
    <TouchableOpacity
      onPress={myOnPress}
      ref={kHoverRef}
      style={[
        type === "primary" && !isHover
          ? styles.buttonPrimary
          : type === "primary" && isHover
          ? styles.buttonPrimaryHover
          : type === "secondary" && isHover
          ? styles.buttonSecondaryHover
          : type === "secondary"
          ? styles.buttonSecondary
          : type === "warning" && isHover
          ? styles.buttonWarningHover
          : type === "warning"
          ? styles.buttonWarning
          : styles.gDrawerContentButtonBlack,

        style,
        padLeft ? { marginStart: kStyleConst.kSpacing * 4 } : null,
        padRight ? { marginEnd: kStyleConst.kSpacing * 4 } : null,
      ]}
      disabled={disabled}
    >
      {isLoading ? (
        <ActivityIndicator color={kColors.white} />
      ) : (
        <>
          {icon && iconPlacement === "left" && <View style={styles.iconLeftView}>{icon}</View>}
          <Text
            style={[
              type === "primary"
                ? fonts.ButtonText_White
                : (type === "warning" || type === "secondary") && isHover
                ? styles.buttonTextWhite
                : type === "warning"
                ? styles.buttonWarningText
                : styles.gDrawerContentButtonText,
              textStyle,
            ]}
            numberOfLines={1}
            ellipsizeMode="tail"
          >
            {text.toUpperCase()}
          </Text>
          {icon && iconPlacement === "right" && <View style={styles.iconRightView}>{icon}</View>}
        </>
      )}
      {disabled && <View style={styles.disabledView} />}
    </TouchableOpacity>
  );
};

export default StandardButton;

const defaultButtonStyle: StyleProp<ViewStyle> = {
  ...kStyleConst.kStandardButtons,
  paddingVertical: kStyleConst.kSpacing * 3,
  paddingHorizontal: kStyleConst.kSpacing * 5,
  borderRadius: kStyleConst.kBorderRadius,
  borderWidth: 2,
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
};

const styles = StyleSheet.create({
  buttonPrimary: {
    ...defaultButtonStyle,
    backgroundColor: kColors.brandDarkGreen,
    borderWidth: 0,
  },
  buttonPrimaryHover: {
    ...defaultButtonStyle,
    backgroundColor: kColors.brandDarkGreenHover,
    borderWidth: 0,
  },
  buttonSecondary: {
    ...defaultButtonStyle,
    borderWidth: 1,
  },
  buttonSecondaryHover: {
    ...defaultButtonStyle,
    backgroundColor: kColors.brandDarkGreen,
    borderColor: kColors.brandDarkGreen,
    borderWidth: 1,
  },
  buttonTextWhite: {
    color: kColors.white,
  },
  buttonWarning: {
    ...defaultButtonStyle,
    borderColor: kColors.secondaryRed,
  },
  buttonWarningHover: {
    ...defaultButtonStyle,
    backgroundColor: kColors.secondaryRed,
    borderColor: kColors.secondaryRed,
  },
  buttonWarningText: {
    color: kColors.secondaryRed,
  },
  disabledView: {
    backgroundColor: kColors.white,
    bottom: 0,
    left: 0,
    opacity: kStyleConst.kAddToCartDisabledOpacity,
    position: "absolute",
    right: 0,
    top: 0,
    zIndex: 5,
  },
  gDrawerContentButtonBlack: {
    ...kStyleConst.kStandardButtons,
    borderColor: kColors.black,
    borderRadius: kStyleConst.kBorderRadius,
    borderWidth: 2,
    justifyContent: "center",
    marginBottom: 4 * kStyleConst.kSpacing,
  },
  gDrawerContentButtonText: {
    ...kFonts.body,
    alignSelf: "center",
  },
  iconLeftView: {
    height: kStyleConst.kSpacing * 3 + 2,
    marginEnd: kStyleConst.kSpacing * 1.5,
  },
  iconRightView: {
    height: kStyleConst.kSpacing * 3 + 2,
    marginStart: kStyleConst.kSpacing * 1.5,
  },
});
